import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Download, FileOutput, FileSpreadsheet } from 'lucide-react';

/**
 * Read-only preview of a budget/proposal, with export buttons for
 * PDF / DOC / Excel. Rendering-only — all logic (status badge,
 * currency formatting, export) is injected by the parent.
 */
export default function ViewBudgetModal({
  open,
  onOpenChange,
  budget,
  formatCurrency,
  getStatusBadge,
  onExport,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Proposta {budget?.proposal_number || ''}</DialogTitle>
        </DialogHeader>

        {budget && (
          <div className="space-y-6 mt-4">
            <div className="text-center border-b pb-4">
              <h2 className="text-xl font-bold text-emerald-800">QUATRO FOLHAS CONSULTORIA LTDA</h2>
              <p className="text-sm text-gray-500 italic">"O sucesso do seu negócio faz parte do nosso plano!"</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm"><strong>Cliente:</strong> {budget.client_name}</p>
                <p className="text-sm"><strong>Empresa:</strong> {budget.company || '-'}</p>
                <p className="text-sm"><strong>Contato:</strong> {budget.contact_person || '-'}</p>
              </div>
              <div>
                <p className="text-sm"><strong>Projeto:</strong> {budget.project_name}</p>
                <p className="text-sm"><strong>Localização:</strong> {budget.project_location || '-'}</p>
                <p className="text-sm"><strong>Status:</strong> {getStatusBadge(budget.status)}</p>
              </div>
            </div>

            {budget.project_description && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Descrição do Projeto</h4>
                <p className="text-sm text-gray-700">{budget.project_description}</p>
              </div>
            )}

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Escopo do Projeto</h4>
              {budget.phases?.map((phase, idx) => (
                <div key={`vb-phase-${phase.name}-${idx}`} className="mb-3 p-3 bg-gray-50 rounded border-l-4 border-emerald-500">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-emerald-800">{phase.name}</span>
                    <span className="text-sm font-medium">{formatCurrency(phase.value)}</span>
                  </div>
                  {phase.activities?.length > 0 && (
                    <ul className="mt-2 ml-4 text-sm text-gray-600">
                      {phase.activities.map((act, actIdx) => (
                        <li key={`vb-act-${idx}-${actIdx}-${act}`}>• {act}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
              <h4 className="font-semibold text-emerald-800 mb-2">Investimento</h4>
              <div className="flex justify-between text-sm">
                <span>Subtotal:</span>
                <span>{formatCurrency(budget.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Impostos:</span>
                <span>{formatCurrency(budget.taxes)}</span>
              </div>
              <div className="flex justify-between font-bold text-emerald-800 pt-2 border-t border-emerald-300 mt-2">
                <span>TOTAL:</span>
                <span>{formatCurrency(budget.total_value)}</span>
              </div>
            </div>

            {budget.observations?.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Observações</h4>
                <ul className="text-sm text-gray-700">
                  {budget.observations.map((obs, idx) => (
                    <li key={`vb-obs-${idx}-${obs}`}>• {obs}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="text-sm text-gray-600">
              <p><strong>Validade:</strong> {budget.validity_days || 20} dias</p>
              <p><strong>Criado em:</strong> {budget.created_at ? new Date(budget.created_at).toLocaleDateString('pt-BR') : '-'}</p>
            </div>

            <div className="flex justify-center gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => onExport(budget, 'pdf')}>
                <Download className="w-4 h-4 mr-2" />
                PDF
              </Button>
              <Button variant="outline" onClick={() => onExport(budget, 'doc')}>
                <FileOutput className="w-4 h-4 mr-2" />
                DOC
              </Button>
              <Button variant="outline" onClick={() => onExport(budget, 'excel')}>
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
